export interface ResolvedLookupItem {
  id: string;
  code: string | undefined;
  description: string | undefined;
  displayText: string;
  isMedical: boolean;
}

export function resolveLookupItem(item: any, index: number, displayColumn?: string): ResolvedLookupItem {
  if (!item) {
    return { id: `item-${index}`, code: undefined, description: undefined, displayText: '', isMedical: false };
  }

  // 1. Build case-insensitive key map
  const keyMap = new Map<string, string>();
  for (const key of Object.keys(item)) {
    keyMap.set(key.toLowerCase(), key);
  }

  // 2. Extract ID value
  const idField = Object.keys(item).find((key) => key.toLowerCase().endsWith('id'));
  const idValue = idField ? item[idField] : (item.id ?? `item-${index}`);

  // 3. Extract code field
  const codeFieldName = keyMap.get('code') || keyMap.get('cptcode');
  const code = codeFieldName ? item[codeFieldName] : undefined;

  // 4. Extract description field
  let descriptionFieldName: string | undefined;
  if (displayColumn) {
    descriptionFieldName = keyMap.get(displayColumn.toLowerCase());
    // Ignore if displayColumn matches the code field (e.g. 'cptCode') or is exactly 'code'
    if (
      descriptionFieldName &&
      (descriptionFieldName.toLowerCase() === 'code' ||
        (codeFieldName && descriptionFieldName.toLowerCase() === codeFieldName.toLowerCase()))
    ) {
      descriptionFieldName = undefined;
    }
  }

  if (!descriptionFieldName) {
    descriptionFieldName =
      keyMap.get('codewithdescription') ||
      keyMap.get('icddescription') ||
      keyMap.get('icd_description') ||
      keyMap.get('cptdescription') ||
      keyMap.get('cptcodedescription') ||
      keyMap.get('cpt_description') ||
      keyMap.get('cpt2description') ||
      keyMap.get('modifierdescription') ||
      keyMap.get('loincdescription') ||
      keyMap.get('description');
  }

  const description = descriptionFieldName ? item[descriptionFieldName] : undefined;

  // 5. Construct displayText (code - description)
  const codeText = code ? String(code).trim() : '';
  const descriptionText = description ? String(description).trim() : '';

  let displayText = '';
  if (descriptionText && codeText) {
    if (descriptionText.toLowerCase().startsWith(`${codeText.toLowerCase()} -`)) {
      displayText = descriptionText;
    } else if (codeText.toLowerCase() !== descriptionText.toLowerCase()) {
      displayText = `${codeText} - ${descriptionText}`;
    } else {
      displayText = descriptionText;
    }
  } else {
    displayText = descriptionText || codeText || item.name || String(idValue);
  }

  // 6. Determine if this is a medical code set lookup (ICD, CPT, CPT2, modifier, snomed, loinc, hcpcs, cptfilter)
  let type = '';
  if (displayColumn) {
    const match = displayColumn.toLowerCase().match(/^([a-z0-9]+)/);
    if (match) type = match[1];
  }
  if (!type) {
    for (const key of Object.keys(item)) {
      const keyLower = key.toLowerCase();
      if (keyLower.endsWith('description') && keyLower !== 'description') {
        type = key.slice(0, key.length - 'description'.length);
        break;
      }
    }
  }
  type = type.toLowerCase();
  const isMedical = ['icd', 'cpt', 'cpt2', 'modifier', 'snomed', 'loinc', 'hcpcs', 'cptfilter'].includes(type);

  return {
    id: String(idValue),
    code,
    description,
    displayText,
    isMedical,
  };
}
